import type {
  ConnectionPoint,
  Point,
  ShapeStyle,
  ShapeType,
  Size,
} from '../state/types';
import { buildEdgeMidpoints } from './connectionPoints';
import { Shape } from './Shape';

export class Triangle extends Shape {
  readonly type: ShapeType = 'triangle';

  constructor(
    position: Point,
    size: Size,
    style?: Partial<ShapeStyle>,
    text = ''
  ) {
    super(position, size, style, text);
  }

  private getVertices(): { top: Point; bottomLeft: Point; bottomRight: Point } {
    const { x, y } = this.position;
    const { width, height } = this.size;

    return {
      top: { x: x + width / 2, y },
      bottomLeft: { x, y: y + height },
      bottomRight: { x: x + width, y: y + height },
    };
  }

  containsPoint(p: Point): boolean {
    const { top, bottomLeft, bottomRight } = this.getVertices();

    // Use barycentric coordinates to check if point is inside triangle
    const v0 = { x: bottomRight.x - top.x, y: bottomRight.y - top.y };
    const v1 = { x: bottomLeft.x - top.x, y: bottomLeft.y - top.y };
    const v2 = { x: p.x - top.x, y: p.y - top.y };

    const dot00 = v0.x * v0.x + v0.y * v0.y;
    const dot01 = v0.x * v1.x + v0.y * v1.y;
    const dot02 = v0.x * v2.x + v0.y * v2.y;
    const dot11 = v1.x * v1.x + v1.y * v1.y;
    const dot12 = v1.x * v2.x + v1.y * v2.y;

    const invDenom = 1 / (dot00 * dot11 - dot01 * dot01);
    const u = (dot11 * dot02 - dot01 * dot12) * invDenom;
    const v = (dot00 * dot12 - dot01 * dot02) * invDenom;

    return u >= 0 && v >= 0 && u + v <= 1;
  }

  getConnectionPoints(): ConnectionPoint[] {
    const { top, bottomLeft, bottomRight } = this.getVertices();
    const edgeMidpoints = buildEdgeMidpoints(this.id, this.position, this.size);
    const topMidpoint = edgeMidpoints[0]!;
    const bottomMidpoint = edgeMidpoints[2]!;

    return [
      // Vertices
      topMidpoint,
      {
        id: `${this.id}-bottom-left`,
        position: bottomLeft,
        direction: 'bottom',
      },
      {
        id: `${this.id}-bottom-right`,
        position: bottomRight,
        direction: 'bottom',
      },
      // Edge midpoints
      {
        id: `${this.id}-right`,
        position: { x: (top.x + bottomRight.x) / 2, y: (top.y + bottomRight.y) / 2 },
        direction: 'right',
      },
      bottomMidpoint,
      {
        id: `${this.id}-left`,
        position: { x: (top.x + bottomLeft.x) / 2, y: (top.y + bottomLeft.y) / 2 },
        direction: 'left',
      },
    ];
  }

  render(ctx: CanvasRenderingContext2D): void {
    const { top, bottomLeft, bottomRight } = this.getVertices();

    ctx.save();
    ctx.globalAlpha = this.style.opacity;

    // Draw triangle
    ctx.beginPath();
    ctx.moveTo(top.x, top.y);
    ctx.lineTo(bottomRight.x, bottomRight.y);
    ctx.lineTo(bottomLeft.x, bottomLeft.y);
    ctx.closePath();

    // Fill
    ctx.fillStyle = this.style.fill;
    ctx.fill();

    // Stroke
    ctx.strokeStyle = this.style.stroke;
    ctx.lineWidth = this.style.strokeWidth;
    ctx.stroke();

    ctx.restore();

    this.renderText(ctx);
    this.renderSelection(ctx);
  }
}
